/* THE WARD PLAN AND THE COMMAND LINE. WardPlan: a Nightingale plan in fixed plan units, one mark per bed and no colour for
   the normal state, which is the world where a browser cannot draw the world. CommandLine: a mnemonic and Enter. ObjectHead: an
   object's name. Everything is drawn from the pure models (model/ward.ts, commands.ts, objects.ts); nothing here decides what a mark means. */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, navigate } from '../../lib/router.tsx'
import { resolve, suggest, type CommandCtx } from '../model/commands.ts'
import { actionFor, isTyping } from '../model/shortcuts.ts'
import { type ObjectRef } from '../model/objects.ts'
import { BED_MARK_TEXT, planOf, type BedMark, type RowMark } from '../model/ward.ts'

export type AnyMark = BedMark | RowMark

const R = 6

/** One mark's drawing at (cx, cy): a ring, and in it what the mark says. Ink only; a hatch is the one tone. */
function MarkShape({ kind, cx, cy }: { kind: AnyMark; cx: number; cy: number }) {
  const ring = <circle cx={cx} cy={cy} r={R} fill="none" stroke="currentColor" strokeWidth={1.25} strokeDasharray={kind === 'empty' ? '2 2' : undefined} />
  switch (kind) {
    case 'done':
      return (
        <>
          {ring}
          <circle cx={cx} cy={cy} r={2.2} fill="currentColor" />
        </>
      )
    case 'short':
      return (
        <>
          {ring}
          <line x1={cx - 3.5} x2={cx + 3.5} y1={cy} y2={cy} stroke="currentColor" strokeWidth={1.5} />
        </>
      )
    case 'harm':
      return (
        <>
          {ring}
          <path d={`M${cx - 3} ${cy - 3} L${cx + 3} ${cy + 3} M${cx + 3} ${cy - 3} L${cx - 3} ${cy + 3}`} stroke="currentColor" strokeWidth={1.5} />
        </>
      )
    case 'lost':
      return <circle cx={cx} cy={cy} r={R} fill="currentColor" stroke="currentColor" strokeWidth={1.25} />
    case 'live':
      return (
        <>
          {ring}
          <path d={`M${cx} ${cy - R} A${R} ${R} 0 0 0 ${cx} ${cy + R} Z`} fill="currentColor" />
        </>
      )
    case 'withdrawn':
      return <circle cx={cx} cy={cy} r={R} fill="url(#ward-hatch)" stroke="currentColor" strokeWidth={1.25} />
    default:
      return ring
  }
}

/** A mark on its own, for a column row or a key. */
export function Mark({ kind, label }: { kind: AnyMark; label: string }) {
  return (
    <svg className="mark-glyph" viewBox="-8 -8 16 16" width="16" height="16" {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}>
      <MarkShape kind={kind} cx={0} cy={0} />
    </svg>
  )
}

/** The hatch every drawing shares, defined once. It takes no room. */
export function Defs() {
  return (
    <svg className="defs" width="0" height="0" aria-hidden="true" focusable="false">
      <defs>
        <pattern id="ward-hatch" patternUnits="userSpaceOnUse" width="3" height="3" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="3" stroke="currentColor" strokeWidth="1" />
        </pattern>
      </defs>
    </svg>
  )
}

/** The Nightingale plan. A bed is a link to its view. `marks[i]` is bed i + 1's mark; `minutes[i]` its corridor minutes. */
export function WardPlan({ count, marks, minutes, caption }: { count: number; marks: readonly BedMark[]; minutes: readonly number[] | null; caption: string }) {
  const plan = useMemo(() => planOf(count), [count])
  const title = useId()
  return (
    <figure className="ward-fig">
      <svg className="ward-plan" viewBox={`0 0 ${plan.width} ${plan.height}`} role="group" aria-labelledby={title}>
        <title id={title}>{caption}</title>
        <rect x={0.5} y={0.5} width={plan.width - 1} height={plan.height - 1} fill="none" stroke="currentColor" strokeWidth={2} />
        <line x1={plan.aisle.x} x2={plan.aisle.x} y1={8} y2={plan.station.y - 8} stroke="var(--line-strong)" strokeWidth={1} strokeDasharray="1 4" />
        {plan.beds.map((b) => {
          const mark = marks[b.n - 1] ?? 'empty'
          const head = b.side === 'west' ? b.x : b.x + b.w
          return (
            <Link key={b.n} to={`/bed/${b.n}`} aria-label={`Bed ${b.n}: ${BED_MARK_TEXT[mark]}${minutes && minutes[b.n - 1] !== undefined ? `; ${minutes[b.n - 1]} min from the supply point` : ''}`} className="bed-link">
              <g>
                <rect x={b.x} y={b.y} width={b.w} height={b.h} fill="var(--bg)" stroke="currentColor" strokeWidth={1} />
                <line x1={head} x2={head} y1={b.y} y2={b.y + b.h} stroke="currentColor" strokeWidth={3} />
                <g className="bed-mark" color="currentColor">
                  <MarkShape kind={mark} cx={b.cx} cy={b.cy} />
                </g>
                <text x={b.side === 'west' ? b.x + b.w + 3 : b.x - 3} y={b.y + 8} textAnchor={b.side === 'west' ? 'start' : 'end'} className="bed-no">
                  {b.n}
                </text>
              </g>
            </Link>
          )
        })}
        <rect x={plan.station.x} y={plan.station.y} width={plan.station.w} height={plan.station.h} fill="url(#ward-hatch)" stroke="currentColor" strokeWidth={1} />
        <text x={plan.station.x + plan.station.w / 2} y={plan.station.y + plan.station.h + 11} textAnchor="middle" className="bed-no">
          STATION
        </text>
      </svg>
    </figure>
  )
}

/** The command line. Typing finds a mnemonic; Enter executes the line; the arrows pick from what typing found. */
export function CommandLine({ ctx, onAct }: { ctx: CommandCtx; onAct: (act: 'dark' | 'light' | 'signout') => void }) {
  const [text, setText] = useState('')
  const [focus, setFocus] = useState(false)
  const [sel, setSel] = useState(-1)
  const [refusal, setRefusal] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const found = useMemo(() => suggest(text, ctx), [text, ctx])
  const listId = useId()

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (actionFor({ key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, altKey: e.altKey, typing: isTyping(e.target) }) === 'command') {
        e.preventDefault()
        input.current?.focus()
      }
    }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [])

  const fill = (usage: string) => {
    // A mnemonic whose example names a subject (BED 7, RUN 2, CMP v3 v4) is filled up to the subject, which the reader types.
    setText(/\d/.test(usage) ? `${usage.split(' ')[0]} ` : usage)
    setSel(-1)
    setRefusal(null)
    input.current?.focus()
  }

  const execute = () => {
    const out = resolve(text, ctx)
    if (out.kind === 'refuse') return setRefusal(out.message)
    setRefusal(null)
    setText('')
    setSel(-1)
    input.current?.blur()
    if (out.kind === 'go') navigate(out.to)
    else onAct(out.act)
  }

  const open = focus || text !== ''
  return (
    <div className="cmdline">
      <form
        className="cmdline-form"
        role="search"
        onSubmit={(e) => {
          e.preventDefault()
          if (sel >= 0 && found[sel]) return fill(found[sel].usage)
          execute()
        }}
      >
        <label htmlFor="command-line" className="cmd-prompt">
          COMMAND
        </label>
        <input
          id="command-line"
          ref={input}
          value={text}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={sel >= 0 ? `${listId}-${sel}` : undefined}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          onChange={(e) => {
            setText(e.target.value)
            setSel(-1)
            setRefusal(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setSel((s) => Math.min(found.length - 1, s + 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setSel((s) => Math.max(-1, s - 1))
            } else if (e.key === 'Escape') {
              setText('')
              setSel(-1)
              setRefusal(null)
              input.current?.blur()
            }
          }}
        />
        <button type="submit" className="cmd-go">
          GO
        </button>
      </form>
      {refusal ? (
        <p className="cmd-refusal refusal" role="alert">
          {refusal}
        </p>
      ) : null}
      {open && !refusal ? (
        <ul className="cmd-list" id={listId} role="listbox" aria-label="Mnemonics" onMouseDown={(e) => e.preventDefault()}>
          {found.length === 0 ? <li className="faint">Nothing begins or says “{text.trim().slice(0, 24)}”.</li> : null}
          {found.map((m, i) => (
            <li key={m.usage} id={`${listId}-${i}`} role="option" aria-selected={i === sel}>
              <button type="button" onClick={() => fill(m.usage)} className={i === sel ? 'is-sel' : ''}>
                <b>{m.usage}</b>
                <span>{m.gist}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/** An object's name: its kind in small capitals and its title in the display face. */
export function ObjectHead({ object, children }: { object: ObjectRef; children?: ReactNode }) {
  return (
    <header className="object-head">
      <div className="kicker">
        {object.kind.toUpperCase()}
        {object.id ? ` · ${object.id}` : ''}
      </div>
      <h1 className="title">{object.title}</h1>
      {children ? <div className="object-lede">{children}</div> : null}
    </header>
  )
}
